import { BoltIcon } from "@heroicons/react/20/solid";
import {
    ActionIcon,
    Badge,
    Card,
    Flex,
    Group,
    LoadingOverlay,
    Stack,
    Text,
    Tooltip
} from "@mantine/core";
import { IconCalendar, IconClock, IconTarget } from "@tabler/icons-react";
import { format } from "date-fns";
import { useSignalAutomations } from "../../hooks/QueryHooks";
import cn from "../../utils/styleHelpers";
import TruncatedTextTooltip from "../ui/truncate/TruncatedText";

type Props = {
    workspace: any;
    account: any;
    containerClass?: string;
};

const AccountSignalsOverview = ({
    workspace,
    account,
    containerClass,
}: Props) => {
    const automations = useSignalAutomations({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
        retry: 0,
    }) as any;

    // Filter automations that this account has triggered
    const accountAutomations = Array.isArray(automations?.data)
        ? automations.data.filter((automation: any) => automation?.entity_relation === "accounts")
        : [];

    if (automations?.isLoading) {
        return (
            <div className={cn("relative", containerClass)}>
                <LoadingOverlay visible />
                <div className="h-64" />
            </div>
        );
    }

    console.log('=== AccountSignalsOverview Debug ===');
    console.log('Raw automations data:', automations?.data);
    console.log('Filtered accountAutomations:', accountAutomations);
    console.log('Sample automation object:', accountAutomations[0]);
    console.log('====================================');

    const accountName = account?.name || account?.display_name || account?.external_id || 'Unknown Account';

    return (
        <div className={cn("w-full", containerClass)}>
            <Stack gap={10} bg="white" className="py-4 border-b border-gray-100 w-full rounded-t-md px-3">
                <Flex align="center" className="self-stretch" justify="space-between" gap={5}>
                    <Flex align="center" gap={7}>
                        <ActionIcon color="main.5" variant="light" size="md">
                            <BoltIcon className="w-7 rounded-md h-7 p-1" />
                        </ActionIcon>
                        <Text color="text.9" size="20" fw={600}>Signal Automations</Text>
                    </Flex>
                    <Badge variant="light" c="main.6" size="sm">
                        {accountAutomations.length} Active
                    </Badge>
                </Flex>
                <Text size="sm" c="text.4" className="px-2">
                    Automations triggered for {accountName}
                </Text>
            </Stack>

            <div className="bg-white p-3 rounded-b-lg overflow-hidden">
                {accountAutomations.length > 0 ? (
                    <div className="p-md">
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 auto-fit-300">
                            {accountAutomations.map((automation: any) => (
                                <Card
                                    key={automation.id}
                                    className="border border-gray-200 hover:shadow-sm transition-shadow min-w-[300px]"
                                >
                                    <Stack gap="md">
                                        {/* Header with name and status */}
                                        <Flex justify="space-between" align="flex-start">
                                            <div className="flex-1">
                                                <TruncatedTextTooltip width="200px" size="md" fw={600} c="text.9" className="truncate max-w-[200px] pr-4">
                                                    {automation.name}
                                                </TruncatedTextTooltip>
                                                {automation.description && (
                                                    <Text size="sm" c="text.6" mt="xs" className="line-clamp-2">
                                                        {automation.description}
                                                    </Text>
                                                )}
                                            </div>
                                            <Badge
                                                variant="light"
                                                color={automation.status === 'active' ? 'green' : 'gray'}
                                                size="sm"
                                                className="ml-2 flex-shrink-0"
                                            >
                                                {automation.status}
                                            </Badge>
                                        </Flex>

                                        {/* Footer with metadata and actions */}
                                        <Flex justify="space-between" align="center" className="pt-3 border-t border-gray-100">
                                            <Group gap="lg">
                                                {automation.created_at && (
                                                    <Group gap="xs">
                                                        <IconCalendar size={14} color="#9ca3af" />
                                                        <Text size="xs" c="text.5">
                                                            {format(new Date(automation.created_at), 'MMM dd, yyyy')}
                                                        </Text>
                                                    </Group>
                                                )}

                                                {automation.updated_at && automation.updated_at !== automation.created_at && (
                                                    <Group gap="xs">
                                                        <IconClock size={14} color="#9ca3af" />
                                                        <Text size="xs" c="text.5">
                                                            Updated {format(new Date(automation.updated_at), 'MMM dd')}
                                                        </Text>
                                                    </Group>
                                                )}
                                            </Group>

                                            <Tooltip label="View details">
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="gray"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('View details for automation:', automation);
                                                    }}
                                                >
                                                    <IconTarget size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Flex>
                                    </Stack>
                                </Card>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="p-8 text-center">
                        <IconTarget size={48} color="#cbd5e1" className="mx-auto mb-4" />
                        <Text size="sm" c="text.3" fw={500} mb="xs">
                            No Signal Automations
                        </Text>
                        <Text size="xs" c="text.4">
                            {accountName} hasn&apos;t triggered any signal automations yet.
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountSignalsOverview;
